/**
 * Owner: Masa Bayu
 * File: QxScreenStates.tsx
 * Date:Time: 2026-09-17 18:42
 * Description: Universal UI states — Skeleton · Empty · Error · Offline (UI/UX Spec D).
 */

export type QxScreenStateKind = 'loading' | 'empty' | 'error' | 'offline';

export interface QxSkeletonProps {
  readonly label?: string;
  readonly rows?: number;
  readonly className?: string;
}

export function QxSkeleton({
  label = 'Loading',
  rows = 3,
  className = '',
}: QxSkeletonProps) {
  const count = Math.max(1, Math.min(rows, 8));
  return (
    <div
      className={`qx-skeleton ${className}`.trim()}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="qx-skeleton__row" />
      ))}
    </div>
  );
}

export interface QxEmptyStateProps {
  readonly title: string;
  readonly description?: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void;
}

export function QxEmptyState({
  title,
  description,
  actionLabel,
  onAction,
}: QxEmptyStateProps) {
  return (
    <div className="qx-empty" role="status">
      <h3 className="qx-empty__title">{title}</h3>
      {description !== undefined ? <p className="qx-empty__body">{description}</p> : null}
      {actionLabel !== undefined && onAction !== undefined ? (
        <button type="button" className="qx-cta qx-cta--primary" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export interface QxErrorStateProps {
  readonly title?: string;
  readonly description?: string;
  readonly onRetry?: () => void;
}

export function QxErrorState({
  title = 'Something went wrong',
  description = 'This section could not load. Other parts of the page remain usable.',
  onRetry,
}: QxErrorStateProps) {
  return (
    <div className="qx-error" role="alert">
      <h3 className="qx-error__title">{title}</h3>
      <p className="qx-error__body">{description}</p>
      {onRetry !== undefined ? (
        <button type="button" className="qx-cta qx-cta--secondary" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export interface QxOfflineBannerProps {
  readonly message?: string;
  readonly libraryPath?: string;
  readonly onGoLibrary?: () => void;
}

export function QxOfflineBanner({
  message = 'You are offline. Cached content may still be available.',
  libraryPath = '/library',
  onGoLibrary,
}: QxOfflineBannerProps) {
  return (
    <div className="qx-offline" role="status">
      <p className="qx-offline__body">{message}</p>
      {onGoLibrary !== undefined ? (
        <button
          type="button"
          className="qx-cta qx-cta--secondary"
          onClick={onGoLibrary}
          data-path={libraryPath}
        >
          Open Library
        </button>
      ) : null}
    </div>
  );
}
